/**
 * The Comms Guide's markdown: heading anchors, its sections and the search.
 *
 * The guide is one markdown document served by service-comms
 * (`knowledge/comms_guide.md` in the services repo): `# Bhanzu Comms Guide`,
 * then one `##` section per module with `###` subsections. The same text
 * answers BhanzuBot's questions, and its links point at `/guide#<anchor>`,
 * so the anchor rule here must match the services test exactly.
 */

/**
 * A heading's anchor: markdown marks (`` ` * _ ``) dropped, lower case,
 * anything but letters, digits, white space and hyphens removed, trimmed,
 * white space runs as one hyphen, repeated hyphens folded. Pinned on both
 * sides: services `tests/test_comms_guide.py` `slug` makes the same ids.
 *
 * @param {string} text  the heading as written
 * @returns {string}
 */
export const slugify = (text) =>
    String(text ?? '')
        .replace(/[`*_]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-{2,}/g, '-')

/** A heading's words for the contents list: the text without markdown marks. */
export const headingLabel = (text) =>
    String(text ?? '')
        .replace(/[`*_]/g, '')
        .trim()

// A heading line: as the services test reads them.
const HEADING = /^(#{1,6})\s+(.+?)\s*$/
const FENCE = /^\s*(```|~~~)/

/**
 * Split the guide into its title, the text before the first section, and
 * the sections: each `##` heading with everything up to the next one (its
 * `###` subsections included), its anchor, and the anchors of its
 * subsections for the contents list. Headings inside code fences are text.
 *
 * @param {string} markdown
 * @returns {{title: string, intro: string, sections: Array<{id: string,
 *     title: string, markdown: string, subsections: Array<{id: string,
 *     title: string}>}>}}
 */
export const parseGuide = (markdown) => {
    let title = ''
    const intro = []
    const sections = []
    let current = null
    let inFence = false
    String(markdown ?? '')
        .split('\n')
        .forEach((line) => {
            if (FENCE.test(line)) inFence = !inFence
            const heading = inFence ? null : HEADING.exec(line)
            const level = heading ? heading[1].length : 0
            if (level === 1 && !title && !current) {
                title = headingLabel(heading[2])
                return
            }
            if (level === 2) {
                current = {
                    id: slugify(heading[2]),
                    title: headingLabel(heading[2]),
                    lines: [line],
                    subsections: [],
                }
                sections.push(current)
                return
            }
            if (level === 3 && current)
                current.subsections.push({
                    id: slugify(heading[2]),
                    title: headingLabel(heading[2]),
                })
            ;(current ? current.lines : intro).push(line)
        })
    return {
        title,
        intro: intro.join('\n').trim(),
        sections: sections.map(({ lines, ...section }) => ({
            ...section,
            markdown: lines.join('\n').trim(),
        })),
    }
}

/**
 * The sections whose text (heading or body) contains `query`, ignoring
 * case; every section for an empty query.
 *
 * @param {Array<{markdown: string}>} sections
 * @param {string} query
 */
export const filterSections = (sections, query) => {
    const needle = String(query ?? '')
        .trim()
        .toLowerCase()
    if (!needle) return sections
    return sections.filter((section) =>
        section.markdown.toLowerCase().includes(needle)
    )
}
